import React, { FC, useCallback, useContext, useMemo } from 'react';
import { Redirect } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader/PageHeader';
import { useTranslation } from 'react-i18next';
import { config } from '../config';
import Launch from '@mui/icons-material/Launch';
import Button from '@mui/material/Button';
import { apiAuth } from '../resources/api/apiAuth';
import { apiZendesk, IZendeskTicket } from '../resources/api/apiZendesk';
import { useZendeskRedirect } from '../hooks/useZendeskRedirect';
import Box from '@mui/system/Box';
import { UrlQueryDataGrid } from '../components/UrlQueryDataGrid/UrlQueryDataGrid';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';
import { Colors } from '../colors';
import { CurrentUserContext } from '../contexts/CurrentUserContext';
import { getDateValueFormatter } from '../valueFormatters/DateValueFormatter';
import { DateFormat } from '../helpers/dateFormat';
import { CellContentTooltip } from '../components/CellContentTooltip/CellContentTooltip';
import { createServerSidePaginatedFetcher, IServerSidePaginatedFetcherParams } from '../contexts/Fetcher';
import { useIsGuardedRouteAllowed } from '../resources/GuardedRouteLink';
import { authenticatedRoot } from '../routers/AuthenticatedRoutes';

const { useZendeskSso } = apiAuth;

interface ISupportTicketRow {
  id: string;
  subject: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export const TicketsFetcher = createServerSidePaginatedFetcher(() => {
  const { userInfo } = useContext(CurrentUserContext);
  const { execute } = apiZendesk.useGetUserTickets(userInfo?.id, userInfo?.zendesk_id);

  return useCallback(
    (pagination: IServerSidePaginatedFetcherParams) => {
      if (!userInfo?.zendesk_id) {
        return Promise.resolve({
          message: 'User tickets retrieved successfully',
          error: false,
          status_code: 200,
          payload: {
            tickets: [],
            total_items: 0,
            page: 1,
            page_size: 10,
          },
        });
      }
      return execute(pagination);
    },
    [execute, userInfo?.zendesk_id]
  );
});

const SupportPageContent: FC = () => {
  const { t } = useTranslation();
  const { Guard } = useZendeskSso();
  const zendeskRedirect = useZendeskRedirect();
  const { data } = useContext(TicketsFetcher.Context);

  const tickets = useMemo<ISupportTicketRow[]>(() => {
    if (!data?.payload.tickets) {
      return [];
    }
    return data.payload.tickets.map((ticket: IZendeskTicket) => ({
      id: ticket.id.toString(),
      subject: ticket.subject,
      status: ticket.status,
      createdAt: ticket.created_at,
      updatedAt: ticket.updated_at,
    }));
  }, [data]);

  const columns = useMemo<GridColDef[]>(() => {
    return [
      { field: 'id', headerName: 'ID', width: 120 },
      {
        field: 'subject',
        headerName: 'Subject',
        flex: 1,
        minWidth: 220,
        renderCell: (params) => (
          <CellContentTooltip noWrap={true}>
            <span style={{ color: Colors.Primary }}>{params.value}</span>
          </CellContentTooltip>
        ),
      },
      { field: 'status', headerName: 'Status', width: 100 },
      {
        field: 'createdAt',
        headerName: 'Created',
        width: 180,
        valueFormatter: getDateValueFormatter(DateFormat.DateMedium),
      },
      {
        field: 'updatedAt',
        headerName: 'Updated',
        width: 180,
        valueFormatter: getDateValueFormatter(DateFormat.DateMedium),
      },
    ];
  }, []);

  const onZendesk = useCallback(() => {
    zendeskRedirect(config.zenDeskAdminLink, '_blank').then();
  }, [zendeskRedirect]);

  const onRowClick = useCallback(
    (params: GridRowParams) => {
      zendeskRedirect(config.customerSupportTicketLink(Number(params.id)), '_blank').then();
    },
    [zendeskRedirect]
  );

  return (
    <div>
      <PageHeader title={t('layout.navigation.support')}>
        <Button
          href={config.supportNewTicketLink}
          rel="noreferrer noopener"
          target="_blank"
          variant="contained"
          color="primary"
          endIcon={<Launch />}
        >
          {t('support.addButton')}
        </Button>
      </PageHeader>

      {data && (
        <UrlQueryDataGrid
          columns={columns}
          rows={tickets}
          disableExtendRowFullWidth={false}
          autoHeight={true}
          disableSelectionOnClick={true}
          paginationMode={'server'}
          filterMode={'server'}
          sortingMode={'server'}
          rowCount={data.payload.total_items}
          onRowClick={onRowClick}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
          }}
        />
      )}

      <Guard>
        <Box mt={3}>
          <Button onClick={onZendesk} variant={'contained'}>
            {t('support.login')}
          </Button>
        </Box>
      </Guard>
    </div>
  );
};

export const SupportPage: FC = () => {
  const isAllowed = useIsGuardedRouteAllowed('Support', []);

  if (!isAllowed) {
    return <Redirect to={authenticatedRoot} />;
  }

  return (
    <TicketsFetcher.WAF>
      <SupportPageContent />
    </TicketsFetcher.WAF>
  );
};
